// Shri — the AI assistant chat panel + executeAction (payment / attendance /
// client / appointment write-through). Talks to /api/shri/*.
import { useState, useEffect, useRef } from "react";
import { doc, setDoc } from "firebase/firestore";
import { db, LS, track, authHeader } from "../lib/firebase";
import { clientToday, canMarkAttendance, paymentIsCurrent } from "../domain";
import { uid, fmt, getCurrencySymbol } from "../lib/format";
import { markCounterPatch } from "../lib/packages";
import { Icon, useToast } from "../ui/primitives";

// Shri option types whose label carries a leading emoji (see api/shri/state.js) — strip for chat display
const EMOJI_PREFIXED_OPTION_TYPES=['cancel','date','confirm','skip','time'];
// Shared with the "Client info" quick action prefill — keep in sync with quickActions
const CLIENT_INFO_PREFILL="Tell me about ";

export const ShriAgent=({clients,setClients,applyClientAndPayment,appointments,setAppointments,theme,profile,userId,isDark,onNavigate,registerBack,setAttendanceRecords,attendanceRecords,onSessionMilestone})=>{
  const [open,setOpen]=useState(false);
  const [messages,setMessages]=useState([]);
  const [input,setInput]=useState("");
  const [loading,setLoading]=useState(false);
  const [listening,setListening]=useState(false);
  const [sessionId]=useState(()=>uid());
  const [convState,setConvState]=useState({state:"idle",data:{}});
  const [shriOptions,setShriOptions]=useState([]); // buttons shown after each message
  const [lastConvId,setLastConvId]=useState(null); // for 👍👎 rating
  const [lastRating,setLastRating]=useState(null); // 'up'|'down'|null
  const rate=async(dir)=>{
    if(lastRating===dir||!lastConvId)return;
    setLastRating(dir);
    await fetch('https://ufcm.vercel.app/api/shri/rate',{method:'POST',headers:{'Content-Type':'application/json',...(await authHeader())},body:JSON.stringify({convId:lastConvId,rating:dir})}).catch(()=>{});
  };
  // Register back button when shri is open
  useEffect(()=>{
    if(!registerBack)return;
    if(open)registerBack("shri-panel",()=>{setOpen(false);setConvState({state:"idle",data:{}}); });
    else registerBack("shri-panel",null);
  },[open,registerBack]);
  const [aiCallCount,setAiCallCount]=useState(()=>{
    const stored=LS.get("shri_ai_count");
    const date=LS.get("shri_ai_date");
    if(date!==clientToday())return 0;
    return parseInt(stored)||0;
  });
  const lastCallRef=useRef(0);
  const bottomRef=useRef(null);
  const inputRef=useRef(null);
  const recognitionRef=useRef(null);
  const voiceStartingRef=useRef(false);
  const toast=useToast();

  const AI_DAILY_LIMIT=50;
  const RATE_LIMIT_MS=3000;

  const p=theme?.primary||"#0f766e";
  const cb=isDark?"#1e293b":"#fff";
  const bc=isDark?"#334155":"#e5e7eb";
  const tc=isDark?"#f1f5f9":"#1f2937";
  const mc=isDark?"#94a3b8":"#6b7280";
  const clientLabel=profile?.clientLabel||"Client";
  const clientLabelPlural=profile?.clientLabelPlural||"Clients";
  const sessionLabel=profile?.sessionLabel||"Session";
  const rawName=profile?.name||profile?.businessName||"there";
  const userName=rawName.split(' ').map(w=>w.charAt(0).toUpperCase()+w.slice(1).toLowerCase()).join(' ');

  useEffect(()=>{
    if(open&&messages.length===0){
      const hour=new Date().getHours();
      const greeting=hour<12?"Good morning":hour<17?"Good afternoon":"Good evening";
      const active=(clients||[]).filter(c=>c.status!=="inactive").length;
      const todayStr=new Date().toLocaleDateString('en-CA');
      const nowHour=new Date().getHours();
      const nowMin=new Date().getMinutes();
      const nowTotal=nowHour*60+nowMin;

      // Check for missed classes — scheduled today but not marked
      const missedClients=[];
      (appointments||[]).forEach(apt=>{
        if(apt.date!==todayStr||apt.status==="Cancelled")return;
        if(!apt.time)return;
        const [h,m]=(apt.time.split(":")||["0","0"]).map(Number);
        const aptTotal=h*60+m;
        if(aptTotal+30>nowTotal)return; // class hasn't ended yet (30min buffer)
        // Check if attendance was marked
        const attended=(clients||[]).find(c=>c.id===apt.clientId||c.name===apt.clientName);
        if(!attended)return;
        const marked=(attendanceRecords||[]).some(r=>r.clientId===attended.id&&r.date===todayStr)||false;
        if(!marked)missedClients.push({name:apt.clientName||attended.name,time:apt.time,clientId:apt.clientId});
      });

      let greetMsg=`${greeting}, ${userName}! 👋 I'm Shri, your Prabzo assistant. You have ${active} active ${active===1?clientLabel.toLowerCase():clientLabelPlural.toLowerCase()}. How can I help you today?`;

      if(missedClients.length>0){
        const missed=missedClients[0];
        greetMsg=`${greeting}, ${userName}! 👋

🔔 ${missed.name}'s ${missed.time} class ended — attendance not marked yet.

Would you like me to mark it now?`;
        setShriOptions([
          {label:`✅ Yes, mark ${missed.name}`,msg:`mark attendance for ${missed.name}`,type:"action"},
          {label:"⏭️ Skip",msg:"skip",type:"skip"},
        ]);
      }
      setMessages([{role:"assistant",content:greetMsg}]);
    }
  },[open]);

  useEffect(()=>{
    if(bottomRef.current)bottomRef.current.scrollIntoView({behavior:"smooth"});
  },[messages,loading]);

  // ── Execute actions returned by the engine ────────────────────────────────
  const matchClient=(action)=>(clients||[]).find(c=>
    c.id===action.clientId ||
    (c.name||"").toLowerCase()===action.clientName?.toLowerCase() ||
    (action.clientName&&(c.name||"").toLowerCase().includes(action.clientName.toLowerCase())));
  // add-payment / mark-attendance / create-client route through the app's shared
  // guarded path (applyClientAndPayment / canMarkAttendance / markCounterPatch) —
  // same semantics as the UI.
  const executeAction=async(action)=>{
    if(!action)return;
    try{
      switch(action.action){
        case"navigate":
          onNavigate(action.page);
          break;

        case"createClient":{
          // The add-client conversation only collects name/phone/service — the
          // server always sends packageStyle:'none' with zero numerics, so a
          // Shri-created client is package-less. Build exactly that (no dangling
          // currentPackageId, no deprecated attendanceHistory array).
          const newClient={
            id:uid(),name:action.name,mobile:action.mobile||"",email:"",
            serviceAvailed:action.service||"",status:"active",joiningDate:clientToday(),
            package:{type:"none",totalAmount:0},packagePrice:0,
            classesRemaining:0,classesAttended:0,paid:0,
            currentPackageId:null,packageHistory:[],
          };
          setClients(prev=>[...prev,newClient]);
          track('client_added',{provider:'shri'});
          toast(`✅ ${action.name} added`,"success");
          if(userId){
            const{id,...data}=newClient;
            setDoc(doc(db,"users",userId,"clients",id),data)
              .catch(e=>{console.error("Shri createClient save:",e);toast("Client didn't sync — check your connection.","warning");});
          }
          break;
        }

        case"markAttendance":{
          const client=matchClient(action);
          if(!client){toast(`${clientLabel} "${action.clientName}" not found`,"error");return;}
          const attDate=action.date||clientToday();
          const g=canMarkAttendance(client,attDate,{existingForClient:attendanceRecords||[],now:new Date()});
          if(!g.ok){
            toast(
              g.reason==="future"?"Can't mark a future date."
              :g.reason==="duplicate"?`Already marked for ${client.name} on ${attDate} ✅`
              :`${client.name}'s package needs renewal.`,
              g.reason==="duplicate"?"info":"warning"
            );
            return;
          }
          const recId=uid();
          const rec={id:recId,clientId:client.id,clientName:client.name,date:attDate,time:"",note:action.note||""};
          if(setAttendanceRecords)setAttendanceRecords(prev=>[rec,...(prev||[])]);
          setClients(prev=>prev.map(c=>c.id===client.id?{...c,...markCounterPatch(c)}:c)); // App-level setClients persists the full doc
          if(userId){
            const{id,...recData}=rec;
            setDoc(doc(db,"users",userId,"attendance",recId),recData)
              .catch(e=>{console.error("Shri attendance save:",e);toast("Attendance didn't sync — it'll retry when you're online.","warning");});
          }
          track('attendance_marked',{provider:'shri'});
          toast(`✅ Attendance marked for ${client.name}`,"success");
          const totalMarked=(parseInt(LS.get("prabzo_sessions_marked")||"0"))+1;
          LS.set("prabzo_sessions_marked",totalMarked);
          if(!LS.get("prabzo_rated")&&[7,21,50].includes(totalMarked))setTimeout(()=>onSessionMilestone?.(),2000);
          onNavigate("sessions");
          break;
        }

        case"addPayment":{
          const client=matchClient(action);
          if(!client){toast(`${clientLabel} not found`,"error");return;}
          const amt=parseFloat(action.amount)||0;
          if(amt<=0){toast("Enter a valid amount.","warning");return;}
          const currency=getCurrencySymbol(profile)||"₹";
          const payment={
            id:uid(),clientId:client.id,clientName:client.name,amount:amt,
            date:clientToday(),mode:"Cash",note:action.note||"Via Shri",
            kind:"shri",packageId:client.currentPackageId||"legacy",
          };
          applyClientAndPayment(
            client.id,
            (c)=>({paid:(Number(c.paid)||0)+(paymentIsCurrent(payment,c)?amt:0)}),
            payment,
            toast
          );
          track('payment_added',{provider:'shri'});
          toast(`💰 ${fmt(amt,currency)} recorded for ${client.name}`,"success");
          break;
        }

        case"bookAppointment":{
          const client=matchClient(action);
          if(!client){toast(`${clientLabel} not found`,"error");return;}
          const newApt={id:uid(),clientId:client.id,clientName:client.name,date:action.date||clientToday(),time:action.time||"",type:action.type||sessionLabel,note:action.note||"Booked via Shri",status:"Upcoming",createdAt:new Date().toISOString()};
          setAppointments(prev=>[...prev,newApt]);
          if(userId){await setDoc(doc(db,"users",userId,"appointments",newApt.id),newApt);}
          break;
        }

        default:break;
      }
    }catch(e){
      console.error("Shri action error:",e);
      toast("Action failed. Please try again.","error");
    }
  };

  // ── Send message to Shri Engine ───────────────────────────────────────────
  const sendMessage=async(text,displayText)=>{
    const userMsg=(text||input).trim();
    if(!userMsg||loading)return;
    if(userMsg.toLowerCase()===CLIENT_INFO_PREFILL.trim().toLowerCase()){toast("Type a name first.","warning");return;}

    // ── Rate limit: 1 AI call per 3 seconds ──
    const now=Date.now();
    if(now-lastCallRef.current<RATE_LIMIT_MS){
      toast("Please wait a moment before sending again","warning");
      return;
    }

    setInput("");
    setShriOptions([]); // clear buttons when user sends
    const newMessages=[...messages,{role:"user",content:(displayText||userMsg).trim()}];
    setMessages(newMessages);
    setLoading(true);

    try{
      const history=newMessages.slice(-10).map(m=>({role:m.role,content:m.content}));
      // Drop the heavy per-client fields the engine never reads (schedule rules,
      // free-text notes, full package history) — sent on every chat turn otherwise.
      const slimClients=(clients||[]).map(({schedule,notes,packageHistory,...c})=>c);
      const res=await fetch("https://ufcm.vercel.app/api/shri",{
        method:"POST",
        headers:{"Content-Type":"application/json",...(await authHeader())},
        body:JSON.stringify({
          message:userMsg,
          clients:slimClients,appointments,profile,
          history,sessionId,
          convState,
          clientDate:new Date().toLocaleDateString('en-CA'), // YYYY-MM-DD in user's timezone
          source:"prabzo",
        }),
      });
      const data=await res.json();
      const finalMessage=data.message?.trim()||"I'm here to help! Could you tell me more?";
      setMessages(prev=>[...prev,{role:"assistant",content:finalMessage}]);
      // Update conversation state from response
      if(data.nextState)setConvState(data.nextState);
      else if(data.provider==="local"||data.provider==="local_slot")setConvState({state:"idle",data:{}});
      // Update button options from response
      setShriOptions(data.options||[]);
      if(data.convId){setLastConvId(data.convId);setLastRating(null);}
      if(data.action)await executeAction(data.action);

      // ── Track AI calls — only count when Groq API was actually used ──
      const usedAI = data.provider && !['local','local_slot','state_machine'].includes(data.provider);
      if(usedAI){
        lastCallRef.current=Date.now();
        const newCount=aiCallCount+1;
        setAiCallCount(newCount);
        LS.set("shri_ai_count",newCount);
        LS.set("shri_ai_date",clientToday());
        if(newCount===Math.floor(AI_DAILY_LIMIT*0.8)){
          toast(`${AI_DAILY_LIMIT-newCount} AI queries remaining today`,"warning");
        }
        if(newCount>=AI_DAILY_LIMIT){
          setMessages(prev=>[...prev,{role:"assistant",content:`You've reached today's AI query limit (${AI_DAILY_LIMIT}). Quick actions and data queries still work! Limit resets at midnight. 🌙`}]);
          setLoading(false);
          return;
        }
      }
    }catch(e){
      console.error("shri error:",e);
      const isNetworkError=e.message?.includes("fetch")||e.message?.includes("network")||e.message?.includes("Failed");
      const msg=isNetworkError
        ?"📡 Can't reach the server. Check your connection and try again."
        :"⚠️ Something went wrong. Please try again.";
      setMessages(prev=>[...prev,{role:"assistant",content:msg}]);
      // Reset conversation state on error so user can retry
      setConvState({state:"idle",data:{}});
      setShriOptions([]);
    }
    setLoading(false);
  };

  // ── Voice input ───────────────────────────────────────────────────────────
  const startVoice=()=>{
    const SR=window.SpeechRecognition||window.webkitSpeechRecognition;
    if(!SR){toast("Voice not supported on this browser","warning");return;}
    if(listening){recognitionRef.current?.stop();return;}
    // Guard synchronously — listening only flips true async in onstart, once the
    // mic permission prompt resolves. A second tap before then would fire a second
    // overlapping getUserMedia request and crash the Android WebView's permission
    // callback ("grant()/deny() already called").
    if(voiceStartingRef.current)return;
    voiceStartingRef.current=true;
    const rec=new SR();
    rec.lang="en-US";rec.interimResults=false;
    rec.onstart=()=>{voiceStartingRef.current=false;setListening(true);};
    rec.onend=()=>{voiceStartingRef.current=false;setListening(false);};
    rec.onresult=(e)=>{
      const t=e.results[0][0].transcript;
      setListening(false);
      setInput(t); // show in input for user to confirm before sending
      toast(`🎤 "${t.slice(0,30)}${t.length>30?"...":""}" — tap ➤ to send`,"info");
    };
    rec.onerror=()=>{voiceStartingRef.current=false;setListening(false);};
    recognitionRef.current=rec;
    rec.start();
  };

  const quickActions=[
    // Data queries — instant, no AI
    {label:"Pending payments",msg:"Who has pending payments?",icon:"payments",category:"query"},
    {label:`Today's ${sessionLabel.toLowerCase()}s`,msg:`Any ${sessionLabel.toLowerCase()}s today?`,icon:"appointments",category:"query"},
    {label:"Expiring packages",msg:"Which packages are expiring soon?",icon:"alert",category:"query"},
    {label:"Revenue summary",msg:"Show me my revenue",icon:"reports",category:"query"},
    {label:"Active clients",msg:"How many active clients do I have?",icon:"clients",category:"query"},
    // Actions — guided bot flow
    {label:"Mark attendance",msg:`Mark attendance`,icon:"check",category:"action"},
    {label:"Record payment",msg:"Record a payment",icon:"payments",category:"action"},
    {label:`Add ${clientLabel.toLowerCase()}`,msg:`Add a new ${clientLabel.toLowerCase()}`,icon:"plus",category:"action"},
    {label:"Book appointment",msg:"Book an appointment",icon:"calendar",category:"action"},
    {label:"Client info",msg:CLIENT_INFO_PREFILL,icon:"search",category:"action",prefillOnly:true},
  ];

  return(
    <>
      {/* Floating Button */}
      {!open&&(
        <button onClick={()=>setOpen(true)}
          className="fixed z-40 flex items-center justify-center transition-all"
          style={{bottom:`calc(env(safe-area-inset-bottom) + 76px)`,right:"16px",width:"52px",height:"52px",borderRadius:"16px",background:`linear-gradient(135deg,${p},${p}bb)`,boxShadow:`0 4px 20px ${p}55`}}>
          <span style={{fontSize:"22px"}}>🪷</span>
        </button>
      )}

      {/* Chat Panel */}
      {open&&(
        <div className="fixed inset-0 z-50 flex flex-col" style={{background:isDark?"#0f172a":"#f1f5f9"}}>

          {/* Header */}
          <div className="flex items-center justify-between px-4 border-b flex-shrink-0"
            style={{background:p,paddingTop:`calc(env(safe-area-inset-top) + 0.75rem)`,paddingBottom:"0.75rem"}}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center text-lg" style={{background:"rgba(255,255,255,0.2)"}}>🪷</div>
              <div>
                <p className="font-black text-white text-sm" style={{fontFamily:"'Playfair Display',serif"}}>Shri</p>
                <p className="text-xs" style={{color:"rgba(255,255,255,0.75)"}}>Your Prabzo Assistant</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {aiCallCount>0&&<span className="text-xs px-2 py-1 rounded-lg" style={{background:"rgba(255,255,255,0.15)",color:AI_DAILY_LIMIT-aiCallCount<10?"#fca5a5":"rgba(255,255,255,0.8)"}}>
                {AI_DAILY_LIMIT-aiCallCount} AI left
              </span>}
              <button onClick={()=>{setMessages([]);setConvState({state:"idle",data:{}});setShriOptions([]);}} className="px-3 py-1.5 rounded-xl text-xs font-semibold" style={{background:"rgba(255,255,255,0.15)",color:"rgba(255,255,255,0.9)"}}>Clear</button>
              <button onClick={()=>setOpen(false)} className="p-2 rounded-xl" style={{background:"rgba(255,255,255,0.15)",color:"#fff"}}><Icon name="x" size={16}/></button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.length<=1&&(
              <div className="space-y-3 mb-3">
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wider" style={{color:mc}}>Quick insights</p>
                  <div className="grid grid-cols-2 gap-2">
                    {quickActions.filter(q=>q.category==="query").map((q,i)=>(
                      <button key={i} onClick={()=>sendMessage(q.msg)}
                        className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-semibold border text-left transition-all active:scale-95"
                        style={{background:cb,borderColor:bc,color:tc}}>
                        <Icon name={q.icon} size={14} style={{color:p,flexShrink:0}}/>
                        {q.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wider" style={{color:mc}}>Quick actions</p>
                  <div className="grid grid-cols-2 gap-2">
                    {quickActions.filter(q=>q.category==="action").map((q,i)=>(
                      <button key={i} onClick={()=>{
                        if(q.prefillOnly){setInput(q.msg);setTimeout(()=>inputRef.current?.focus(),0);}
                        else sendMessage(q.msg);
                      }}
                        className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-semibold border text-left transition-all active:scale-95"
                        style={{background:p+"0d",borderColor:p+"40",color:p}}>
                        <Icon name={q.icon} size={14} style={{flexShrink:0}}/>
                        {q.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {messages.filter(m=>m.content?.trim()).map((m,i)=>{
              const isLastAssistant=m.role==="assistant"&&i===messages.length-1;
              return(
              <div key={i} className={`flex items-end gap-2 ${m.role==="user"?"justify-end":"justify-start"}`}>
                {m.role==="assistant"&&(
                  <div className="w-7 h-7 rounded-lg flex-shrink-0 flex items-center justify-center text-xs mb-1" style={{background:p}}>🪷</div>
                )}
                <div className="flex flex-col gap-1 max-w-[82%]">
                  <div className="px-4 py-2.5 text-sm leading-relaxed whitespace-pre-line"
                    style={{background:m.role==="user"?p:cb,color:m.role==="user"?"#fff":tc,borderRadius:m.role==="user"?"18px 18px 4px 18px":"18px 18px 18px 4px",boxShadow:"0 1px 3px rgba(0,0,0,0.08)"}}>
                    {m.content}
                  </div>
                  {isLastAssistant&&lastConvId&&shriOptions.length===0&&(
                    <div className="flex gap-1 pl-1">
                      <button onClick={()=>rate('up')} className="text-xs px-2 py-1 rounded-lg transition-all"
                        style={{background:lastRating==='up'?"#dcfce7":"transparent",color:lastRating==='up'?"#16a34a":mc,border:`1px solid ${lastRating==='up'?"#86efac":"transparent"}`}}>
                        👍
                      </button>
                      <button onClick={()=>rate('down')} className="text-xs px-2 py-1 rounded-lg transition-all"
                        style={{background:lastRating==='down'?"#fee2e2":"transparent",color:lastRating==='down'?"#ef4444":mc,border:`1px solid ${lastRating==='down'?"#fca5a5":"transparent"}`}}>
                        👎
                      </button>
                    </div>
                  )}
                </div>
              </div>
              );
            })}

            {loading&&(
              <div className="flex items-end gap-2">
                <div className="w-7 h-7 rounded-lg flex-shrink-0 flex items-center justify-center text-xs" style={{background:p}}>🪷</div>
                <div className="px-4 py-3 rounded-2xl" style={{background:cb,borderRadius:"18px 18px 18px 4px",boxShadow:"0 1px 3px rgba(0,0,0,0.08)"}}>
                  <div className="flex gap-1 items-center">
                    {[0,1,2].map(i=>(<div key={i} className="w-2 h-2 rounded-full animate-bounce" style={{background:p,animationDelay:`${i*0.15}s`}}/>))}
                  </div>
                </div>
              </div>
            )}
            <div ref={bottomRef}/>
          </div>

          {/* Options buttons — shown after shri responds */}
          {shriOptions.length>0&&!loading&&(
            <div className="flex-shrink-0 px-4 pt-2 pb-1 border-t" style={{borderColor:bc,background:cb}}>
              <div className="flex flex-wrap gap-2">
                {shriOptions.map((opt,i)=>{
                  // Style varies by option type
                  const isCancel=opt.type==='cancel';
                  const isConfirm=opt.type==='confirm';
                  const isSkip=opt.type==='skip';
                  const isType=opt.type==='type';
                  const bg=isConfirm?p:isCancel?"#fef2f2":isSkip||isType?cb:p+"18";
                  const color=isConfirm?"#fff":isCancel?"#ef4444":isSkip||isType?mc:p;
                  const border=isConfirm?"none":isCancel?"1px solid #fecaca":`1px solid ${p}40`;
                  return(
                    <button key={i}
                      onClick={()=>{
                        if(opt.msg==='__type__'){
                          // Focus input for keyboard
                          inputRef.current?.focus();
                          setShriOptions([]);
                        } else {
                          // Only cancel/date/confirm/skip/time options carry an emoji prefix worth stripping —
                          // client/service/amount labels are plain text and must be sent as-is.
                          const displayText=EMOJI_PREFIXED_OPTION_TYPES.includes(opt.type)?opt.label.replace(/^\S+\s*/,''):opt.label;
                          sendMessage(opt.msg,displayText);
                        }
                      }}
                      className="px-3 py-2 rounded-xl text-xs font-semibold transition-all active:scale-95"
                      style={{background:bg,color,border}}>
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="flex-shrink-0 px-4 py-3 border-t"
            style={{background:cb,borderColor:bc,paddingBottom:`calc(env(safe-area-inset-bottom) + 0.75rem)`}}>
            <div className="flex items-center gap-2">
              <input ref={inputRef}
                className="flex-1 px-4 py-2.5 rounded-2xl text-sm border focus:outline-none"
                style={{background:isDark?"#0f172a":"#f9fafb",borderColor:bc,color:tc}}
                placeholder={shriOptions.length>0?"Or type your response...":"Ask Shri..."}
                value={input}
                onChange={e=>setInput(e.target.value)}
                onKeyDown={e=>e.key==="Enter"&&!loading&&sendMessage()}/>
              <button onClick={startVoice}
                className="p-2.5 rounded-2xl flex-shrink-0 transition-all"
                style={{background:listening?"#ef4444":isDark?"#334155":"#f3f4f6",color:listening?"#fff":mc}}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                  <line x1="12" y1="19" x2="12" y2="23"/>
                  <line x1="8" y1="23" x2="16" y2="23"/>
                </svg>
              </button>
              <button onClick={()=>sendMessage()}
                disabled={!input.trim()||loading}
                className="p-2.5 rounded-2xl flex-shrink-0 transition-all"
                style={{background:input.trim()&&!loading?p:"#e5e7eb",color:input.trim()&&!loading?"#fff":"#9ca3af"}}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="22" y1="2" x2="11" y2="13"/>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                </svg>
              </button>
            </div>
            <p className="text-center text-xs mt-2" style={{color:mc}}>🪷 Shri is learning. Double-check critical actions.</p>
          </div>
        </div>
      )}
    </>
  );
};
